import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Flex,
  Space,
  Spin,
  Table,
  Typography,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { getCompanyListCompanies, removeCompanyFromList } from '../api';
import './workspaceInformation/wiPhase1.css';

const { Title, Text, Paragraph } = Typography;

const API = process.env.NODE_ENV === 'production'
  ? 'https://admin.highperformr.ai/api'
  : 'http://localhost:4000/api';

const CompanyListDetails = ({ listId, onBack }) => {
  const [companies, setCompanies] = useState([]);
  const [listInfo, setListInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCompanies();
    loadListInfo();
  }, [listId]);

  const loadListInfo = async () => {
    try {
      const response = await axios.get(`${API}/company-lists/${listId}`, {
        withCredentials: true,
      });
      setListInfo(response.data.data);
    } catch (err) {
      console.error('Error loading list info:', err);
    }
  };

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCompanyListCompanies(listId);
      setCompanies(response.data.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load companies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (companyId) => {
    if (!window.confirm('Are you sure you want to remove this company from the list?')) {
      return;
    }
    try {
      await removeCompanyFromList(listId, companyId);
      await loadCompanies();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to remove company');
    }
  };

  const handleUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);
      formData.append('listId', listId);

      await axios.post(`${API}/company-lists/${listId}/add-companies`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowUpload(false);
      setCsvFile(null);
      await loadCompanies();
    } catch (err) {
      setError(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Company ID',
        key: 'id',
        width: 140,
        render: (_, company) => (
          <Text code>{company.id || company.company_id || '—'}</Text>
        ),
      },
      {
        title: 'Name',
        key: 'name',
        render: (_, company) => {
          const companyLogo = company.company_logo || company.logo || '';
          const companyName = company.name || company.company_name || '—';
          return (
            <Space>
              {companyLogo ? (
                <Avatar src={companyLogo} size={24} shape="square" alt={companyName} />
              ) : null}
              <Text>{companyName}</Text>
            </Space>
          );
        },
      },
      {
        title: 'LinkedIn',
        key: 'linkedin',
        render: (_, company) =>
          company.linkedin_url ? (
            <a
              href={`https://${company.linkedin_url}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {company.linkedin_url}
            </a>
          ) : (
            '—'
          ),
      },
      {
        title: 'Website',
        key: 'website',
        render: (_, company) =>
          company.website ? (
            <a
              href={
                company.website.startsWith('http')
                  ? company.website
                  : `https://${company.website}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {company.website}
            </a>
          ) : (
            '—'
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 90,
        align: 'center',
        render: (_, company) => (
          <Button
            danger
            icon={<DeleteOutlined />}
            title="Remove Company"
            onClick={() => handleRemove(company.id || company.company_id)}
          />
        ),
      },
    ],
    [listId]
  );

  return (
    <div className="wi-page">
      <div className="wi-panel" style={{ maxWidth: 1100 }}>
        <Card bordered={false} className="wi-search-card">
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <Space align="start" size="middle" wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                Back
              </Button>
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {listInfo ? `${listInfo.name || 'List'} — Companies` : 'Companies'}
                </Title>
                {!loading ? (
                  <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                    Total companies: <Text strong>{companies.length}</Text>
                  </Paragraph>
                ) : null}
              </div>
            </Space>
            <Button
              type={showUpload ? 'default' : 'primary'}
              icon={<PlusOutlined />}
              onClick={() => {
                setShowUpload(!showUpload);
                setCsvFile(null);
              }}
            >
              {showUpload ? 'Cancel' : 'Add New Companies'}
            </Button>
          </Flex>
        </Card>

        {showUpload ? (
          <Card title="Upload CSV" style={{ marginTop: 16 }} className="wi-details-card" size="small">
            <Paragraph type="secondary">
              CSV should have columns: linkedin_url, website (optional).
            </Paragraph>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Upload
                accept=".csv"
                maxCount={1}
                beforeUpload={(file) => {
                  setCsvFile(file);
                  return false;
                }}
                onRemove={() => setCsvFile(null)}
                fileList={
                  csvFile
                    ? [
                        {
                          uid: '-1',
                          name: csvFile.name,
                          status: 'done',
                        },
                      ]
                    : []
                }
              >
                <Button icon={<UploadOutlined />}>Select CSV</Button>
              </Upload>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploading}
                disabled={!csvFile}
              >
                Upload & Add Companies
              </Button>
            </Space>
          </Card>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginTop: 16 }}
            closable
            onClose={() => setError(null)}
          />
        ) : null}

        <Card style={{ marginTop: 16 }} className="wi-details-card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" tip="Loading companies…" />
            </div>
          ) : companies.length === 0 ? (
            <Empty description="No companies in this list" />
          ) : (
            <Table
              size="middle"
              rowKey={(row) => row.id || row.company_id}
              dataSource={companies}
              columns={columns}
              pagination={false}
              scroll={{ x: true }}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default CompanyListDetails;
